import CtaSection from '@/components/CtaSection'
import Layout from '@/components/Layout'
import Seo from '@/components/Seo'
import { blogPosts, breadcrumbSchema, whatsappMessages } from '@/lib/site-data'
import {ArrowLeft, Calendar, Clock} from 'lucide-react'
import { Link, Navigate, useParams } from 'react-router-dom'

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
}

function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const post = blogPosts.find(item => item.slug === slug)

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishedAt,
    author: {
      '@type': 'Organization',
      name: 'Muita Mídia LTDA',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Muita Mídia LTDA',
    },
  }

  const crumbs = breadcrumbSchema([
    { name: 'Início', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: post.title, path: `/blog/${post.slug}` },
  ])

  const otherPosts = blogPosts.filter(item => item.slug !== post.slug).slice(0, 3)

  return (
    <Layout>
      <Seo
        title={`${post.title} | Blog Muita Mídia`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        jsonLd={[articleSchema, crumbs]}
      />

      <article className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#7C3AED] hover:underline">
            <ArrowLeft className="h-4 w-4" />
            Voltar para o blog
          </Link>

          <div className="mt-6 flex items-center gap-4 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(post.publishedAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {post.readTime}
            </span>
          </div>

          <h1 className="mt-4 text-3xl font-bold leading-tight text-zinc-900 sm:text-4xl">{post.title}</h1>
          <p className="mt-4 text-base leading-relaxed text-zinc-600">{post.excerpt}</p>

          <div className="mt-10 space-y-8">
            {post.sections.map((section, index) => (
              <div key={section.heading || index}>
                {section.heading ? (
                  <h2 className="text-xl font-bold text-zinc-900">{section.heading}</h2>
                ) : null}
                {section.paragraphs.map((paragraph, pIndex) => (
                  <p key={pIndex} className="mt-3 text-base leading-relaxed text-zinc-700">
                    {paragraph}
                  </p>
                ))}
                {section.list ? (
                  <ul className="mt-4 space-y-2">
                    {section.list.map(item => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#7C3AED]" />
                        <span className="text-base leading-relaxed text-zinc-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </article>

      <CtaSection
        title="Pronto para comprar sua página do Instagram?"
        description="Fale com a equipe da Muita Mídia pelo WhatsApp e tire suas dúvidas antes de decidir."
        message={whatsappMessages.blogCta}
        tone="dark"
      />

      {otherPosts.length > 0 ? (
        <section className="bg-white">
          <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <h2 className="text-xl font-bold text-zinc-900">Continue lendo</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-3">
              {otherPosts.map(item => (
                <Link
                  key={item.slug}
                  to={`/blog/${item.slug}`}
                  className="rounded-2xl border border-zinc-100 bg-zinc-50 p-5 transition-colors hover:border-[#8B5CF6]/40 hover:bg-white"
                >
                  <h3 className="text-sm font-semibold text-zinc-900">{item.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-600">{item.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </Layout>
  )
}

export default BlogPost
